import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Tab, Tabs, Form, Row, Col, Badge, Spinner, Alert } from "react-bootstrap";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  getCalculatedIncomeRecords,
  getCalculatedExpenseRecords,
  getRevenueStatistics,
  getMonthlyRevenueTrends,
  generateRevenueReport,
  updateCollegeFeeConfiguration,
  formatCurrency,
  formatPercentage,
  getCategoryDisplayName,
  INCOME_CATEGORIES,
  EXPENSE_CATEGORIES,
  TRANSACTION_STATUS,
  DEFAULT_FEES,
} from "../services/revenueService";
import {
  getFeeConfigurations,
  updateFeeConfiguration,
  FEE_TYPES,
  formatCurrency as formatFeeAmount
} from "../services/feeConfigService";
import { getAllColleges } from "../services/collegeService";
import { getStudents } from "../services/userService";
import { STUDENT_LEVELS } from "../utils";
import Loader from "../components/Loader";
import { toast } from "react-toastify";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Revenue = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({});
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [students, setStudents] = useState([]);
  const [feeConfigurations, setFeeConfigurations] = useState({});
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    category: "",
    status: "",
  });
  const [updatingCollegeFee, setUpdatingCollegeFee] = useState(null);
  const [updatingFeeConfig, setUpdatingFeeConfig] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [stats, income, expenses, trends, collegeData, studentData, feeConfigs] = await Promise.all([
        getRevenueStatistics(filters),
        getCalculatedIncomeRecords(filters),
        getCalculatedExpenseRecords(filters),
        getMonthlyRevenueTrends(),
        getAllColleges(),
        getStudents(),
        getFeeConfigurations(),
      ]);

      setStatistics(stats);
      setIncomeRecords(income);
      setExpenseRecords(expenses);
      setMonthlyTrends(trends);
      setColleges(collegeData);
      setStudents(studentData);
      setFeeConfigurations(feeConfigs);
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle college fee update
  const handleCollegeFeeUpdate = async (collegeId, newFee) => {
    try {
      setUpdatingCollegeFee(collegeId);
      await updateCollegeFeeConfiguration(collegeId, newFee);
      await fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating college fee:", error);
    } finally {
      setUpdatingCollegeFee(null);
    }
  };

  // Handle fee configuration update
  const handleFeeConfigUpdate = async (feeType, newAmount) => {
    try {
      setUpdatingFeeConfig(feeType);
      await updateFeeConfiguration(feeType, { amount: parseFloat(newAmount) });
      await fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating fee configuration:", error);
    } finally {
      setUpdatingFeeConfig(null);
    }
  };

  // Generate report
  const handleGenerateReport = async () => {
    try {
      const report = await generateRevenueReport(filters.startDate, filters.endDate);
      
      // Create downloadable report
      const reportData = JSON.stringify(report, null, 2);
      const blob = new Blob([reportData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue-report-${filters.startDate.toISOString().split('T')[0]}-to-${filters.endDate.toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  // Chart configurations
  const revenueChartData = {
    labels: monthlyTrends.map(trend => trend.month),
    datasets: [
      {
        label: "Income",
        data: monthlyTrends.map(trend => trend.income),
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.1,
      },
      {
        label: "Expenses",
        data: monthlyTrends.map(trend => trend.expenses),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        tension: 0.1,
      },
      {
        label: "Profit",
        data: monthlyTrends.map(trend => trend.profit),
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        tension: 0.1,
      },
    ],
  };

  const incomeCategoryData = {
    labels: Object.keys(statistics.incomeByCategory || {}).map(cat => getCategoryDisplayName(cat, 'income')),
    datasets: [
      {
        data: Object.values(statistics.incomeByCategory || {}),
        backgroundColor: [
          "#FF6384",
          "#36A2EB", 
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
        ],
      },
    ],
  };

  const expenseCategoryData = {
    labels: Object.keys(statistics.expensesByCategory || {}).map(cat => getCategoryDisplayName(cat, 'expense')),
    datasets: [
      {
        data: Object.values(statistics.expensesByCategory || {}),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56", 
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
        ],
      },
    ],
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-chart-line me-2"></i>
          Revenue Management
        </h2>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={fetchData}>
            <i className="fas fa-sync-alt me-1"></i>
            Refresh
          </Button>
          <Button variant="success" onClick={handleGenerateReport}>
            <i className="fas fa-download me-1"></i>
            Generate Report
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Filters</h5>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleFilterChange('startDate', new Date(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleFilterChange('endDate', new Date(e.target.value))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value={TRANSACTION_STATUS.COMPLETED}>Completed</option>
                  <option value={TRANSACTION_STATUS.PENDING}>Pending</option>
                  <option value={TRANSACTION_STATUS.CANCELLED}>Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        {/* Overview Tab */}
        <Tab eventKey="overview" title="Overview">
          <Row className="mb-4">
            <Col md={3}>
              <Card className="bg-success text-white h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between">
                    <div>
                      <Card.Title>Total Income</Card.Title>
                      <h3>{formatCurrency(statistics.totalIncome)}</h3>
                      <small>{statistics.totalIncomeTransactions} transactions</small>
                    </div>
                    <div className="align-self-center">
                      <i className="fas fa-arrow-up fa-2x"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-danger text-white h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between">
                    <div>
                      <Card.Title>Total Expenses</Card.Title>
                      <h3>{formatCurrency(statistics.totalExpenses)}</h3>
                      <small>{statistics.totalExpenseTransactions} transactions</small>
                    </div>
                    <div className="align-self-center">
                      <i className="fas fa-arrow-down fa-2x"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className={`text-white h-100 ${statistics.netProfit >= 0 ? 'bg-info' : 'bg-warning'}`}>
                <Card.Body>
                  <div className="d-flex justify-content-between">
                    <div>
                      <Card.Title>Net Profit</Card.Title>
                      <h3>{formatCurrency(statistics.netProfit)}</h3>
                      <small>Margin: {formatPercentage(statistics.profitMargin)}</small>
                    </div>
                    <div className="align-self-center">
                      <i className={`fas ${statistics.netProfit >= 0 ? 'fa-chart-line' : 'fa-chart-line-down'} fa-2x`}></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-secondary text-white h-100">
                <Card.Body>
                  <div>
                    <Card.Title>Student Breakdown</Card.Title>
                    <div className="small">
                      <div>Paid Trainees: {statistics.studentBreakdown?.paidTrainees || 0}</div>
                      <div>College Interns: {statistics.studentBreakdown?.collegeInterns || 0}</div>
                      <div>Individual Interns: {statistics.studentBreakdown?.individualInterns || 0}</div>
                      <div>Free Trainees: {statistics.studentBreakdown?.freeTrainees || 0}</div>
                      <div>Employees: {statistics.studentBreakdown?.employees || 0}</div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row className="mb-4">
            <Col lg={8}>
              <Card>
                <Card.Header>
                  <h5>Revenue Trends (Last 12 Months)</h5>
                </Card.Header>
                <Card.Body>
                  <Line data={revenueChartData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="h-100">
                <Card.Header>
                  <h5>Income by Category</h5>
                </Card.Header>
                <Card.Body>
                  <Doughnut data={incomeCategoryData} options={{ responsive: true, maintainAspectRatio: false }} height={300} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Income Tab */}
        <Tab eventKey="income" title="Income">
          <Alert variant="info">
            <strong>Automatic Income Calculation:</strong> Income is automatically calculated based on configurable student fees:
            <ul className="mt-2 mb-0">
              <li><strong>Free Trainees:</strong> No fee (₹0)</li>
              <li><strong>Paid Trainees:</strong> {formatCurrency(feeConfigurations[FEE_TYPES.PAID_TRAINEE]?.amount || 0)} per training</li>
              <li><strong>Individual Interns:</strong> {formatCurrency(feeConfigurations[FEE_TYPES.INDIVIDUAL_INTERNSHIP]?.amount || 0)} per internship</li>
              <li><strong>College Interns:</strong> Fee as per college configuration</li>
            </ul>
          </Alert>

          <Card>
            <Card.Header>
              <h5>Income Records</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomeRecords.map((record) => (
                      <tr key={record.id}>
                        <td>{record.date.toLocaleDateString()}</td>
                        <td>
                          <Badge bg="success">
                            {getCategoryDisplayName(record.category, 'income')}
                          </Badge>
                        </td>
                        <td>{record.studentName}</td>
                        <td>{record.courseName}</td>
                        <td>{formatCurrency(record.amount)}</td>
                        <td>
                          <Badge bg={record.status === TRANSACTION_STATUS.COMPLETED ? 'success' : 'warning'}>
                            {record.status}
                          </Badge>
                        </td>
                        <td>{record.paymentMethod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Expenses Tab */}
        <Tab eventKey="expenses" title="Expenses">
          <Alert variant="info">
            <strong>Automatic Expense Calculation:</strong> Expenses are automatically calculated from:
            <ul className="mt-2 mb-0">
              <li><strong>Tutor Salaries:</strong> From salary configurations</li>
              <li><strong>Employee Salaries:</strong> From salary configurations</li>
              <li><strong>Individual Intern Stipends:</strong> {formatCurrency(feeConfigurations[FEE_TYPES.INDIVIDUAL_INTERN_STIPEND]?.amount || 0)}/month for interns not assigned to colleges</li>
            </ul>
          </Alert>

          <Card>
            <Card.Header>
              <h5>Expense Records</h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Vendor/Employee</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseRecords.map((record) => (
                      <tr key={record.id}>
                        <td>{record.date.toLocaleDateString()}</td>
                        <td>
                          <Badge bg="danger">
                            {getCategoryDisplayName(record.category, 'expense')}
                          </Badge>
                        </td>
                        <td>{record.description}</td>
                        <td>{record.tutorName || record.employeeName || record.internName || record.vendorName}</td>
                        <td>{formatCurrency(record.amount)}</td>
                        <td>
                          <Badge bg={record.status === TRANSACTION_STATUS.COMPLETED ? 'success' : 'warning'}>
                            {record.status}
                          </Badge>
                        </td>
                        <td>{record.paymentMethod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* Configuration Tab */}
        <Tab eventKey="config" title="Configuration">
          <Row>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>Fee Configuration</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Fee Type</th>
                          <th>Current Amount</th>
                          <th>Category</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(feeConfigurations).map(([feeType, config]) => (
                          <tr key={feeType}>
                            <td>
                              <strong>{config.name}</strong>
                              <br />
                              <small className="text-muted">{config.description}</small>
                            </td>
                            <td>
                              <Badge bg={config.category === 'income' ? 'success' : 'danger'}>
                                {formatCurrency(config.amount)}
                                {config.frequency === 'monthly' && '/month'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={config.category === 'income' ? 'success' : 'danger'}>
                                {config.category.toUpperCase()}
                              </Badge>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                placeholder="Enter amount"
                                defaultValue={config.amount}
                                onBlur={(e) => {
                                  if (e.target.value && e.target.value !== config.amount?.toString()) {
                                    handleFeeConfigUpdate(feeType, e.target.value);
                                  }
                                }}
                                disabled={updatingFeeConfig === feeType}
                              />
                              {updatingFeeConfig === feeType && (
                                <Spinner animation="border" size="sm" className="ms-2" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Alert variant="info" className="small mt-3">
                    <strong>Note:</strong> These fee configurations are applied automatically when calculating revenue. Changes take effect immediately.
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card>
                <Card.Header>
                  <h5>College Internship Fees</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>College</th>
                          <th>Current Fee</th>
                          <th>Students</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {colleges.map((college) => (
                          <tr key={college.id}>
                            <td>{college.name}</td>
                            <td>{formatCurrency(college.internshipFee || 0)}</td>
                            <td>{college.studentCount || 0}</td>
                            <td>
                              <Form.Control
                                type="number"
                                size="sm"
                                placeholder="Enter fee"
                                defaultValue={college.internshipFee || ''}
                                onBlur={(e) => {
                                  if (e.target.value && e.target.value !== college.internshipFee?.toString()) {
                                    handleCollegeFeeUpdate(college.id, e.target.value);
                                  }
                                }}
                                disabled={updatingCollegeFee === college.id}
                              />
                              {updatingCollegeFee === college.id && (
                                <Spinner animation="border" size="sm" className="ms-2" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Alert variant="warning" className="small mt-3">
                    <strong>Note:</strong> College-specific fees override the default individual internship fee for students assigned to that college.
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </div>
  );
};

export default Revenue;